import { TenantEnum } from '@jsmdg/tenancy';
import {
    type ShippingValues,
    type ShippingValuesFields,
    type ShippingValuesPerTenant,
} from '../client/types/shippingValueTypes';
import { type SharedData, type TenantConfig } from './types';

const emptySharedData: SharedData = {
    urls: {
        payback: '',
        termAndConditions: '',
        clubLink: '',
        redemption: '',
        web: {
            label: '',
            href: '',
        },
    },
    contactInfo: {
        phone: {
            label: '',
            number: '',
        },
    },
    brandDisplayName: '',
    voucherExpirationLimitInYears: 3,
};

const JS_DEFAULT = {
    paybackIsEnabled: true,
    nonPaybackSkus: [
        '6143', // book
        '6473', // audio book
        '7608', // audio book
        '7496', // book
    ],
    websiteMarkup: {
        sellerName: 'Jochen Schweizer GmbH',
    },
    shareTitle: 'Jochen Schweizer',
    shippingModalDescription: 'Erlebnisauswahl in der hochwertigen Geschenkbox inkl. 1 Gutschein',
    websiteText: 'www.jochen-schweizer.de',
    redeemText: '3 Jahre Gültigkeit',
    uspPrimaryText: 'Jeder Gutschein für 9.000 Erlebnisse einlösbar',
    uspSecondaryText: 'Gutscheine 3 Jahre gültig und verlängerbar',
    uspTertiaryText: 'Persönlicher Einlöse-Service',
    friendsRabattText: '-10% ab dem 2. Gutschein',
    ...emptySharedData,
    urls: {
        ...emptySharedData.urls,
        clubLink: '/club',
        termAndConditions: '/agb/JS_AGB/l/bdank',
    },
};

const MD_DEFAULT = {
    ...JS_DEFAULT,
    nonPaybackSkus: [],
    websiteMarkup: {
        sellerName: 'mydays GmbH',
    },
    shareTitle: 'mydays',
    websiteText: 'www.mydays.de',
    redeemText: '3 Jahre Gültigkeit',
    urls: {
        ...JS_DEFAULT.urls,
        clubLink: '/club',
        termAndConditions: '/agb/l/hs3ij',
    },
};

const tenantConfigMapping: Record<string, TenantConfig> = {
    [TenantEnum.JS_DE]: JS_DEFAULT,
    [TenantEnum.JS_AT]: {
        ...JS_DEFAULT,
        websiteText: 'www.jochen-schweizer.at',
        redeemText: '10 Jahre Gültigkeit',
        paybackIsEnabled: false,
        nonPaybackSkus: [],
        uspSecondaryText: 'Gutscheine 10 Jahre gültig und verlängerbar',
    },
    [TenantEnum.MD_DE]: MD_DEFAULT,
    [TenantEnum.MD_AT]: {
        ...MD_DEFAULT,
        websiteText: 'www.mydays.at',
        redeemText: '10 Jahre Gültigkeit',
        paybackIsEnabled: false,
        uspSecondaryText: 'Gutscheine 10 Jahre gültig und verlängerbar',
    },
    [TenantEnum.MD_CH]: {
        ...MD_DEFAULT,
        websiteText: 'www.mydays.ch',
        redeemText: '10 Jahre Gültigkeit',
        paybackIsEnabled: false,
        uspSecondaryText: 'Gutscheine 10 Jahre gültig und verlängerbar',
    },
};

// tickets/vouchers sent as letters, shipping time based on Deutsche Post delivery terms defined by law
const ticketsAndVouchersDE: ShippingValuesFields = {
    shipMethodStar: '',
    minShipDays: 3,
    maxShipDays: 4,
    standardShip: 4.9,
    expressShip: 16,
    xMasExpressShip: 8,
    giftShip: 7.9,
    minShipDaysGermany: 3,
    maxShipDaysGermany: 4,
    minShipDaysEurope: 5,
    maxShipDaysEurope: 6,
};

// boxes sent as Päckchen, shipping time based on DPD contractual obligations
const erlebnisBoxDE: ShippingValuesFields = {
    shipMethodStar: '',
    minShipDays: 1,
    maxShipDays: 2,
    standardShip: 4.9,
    xMasExpressShip: 8,
    expressShip: 16,
    minShipDaysGermany: 1,
    maxShipDaysGermany: 2,
    minShipDaysEurope: 5,
    maxShipDaysEurope: 6,
};

const ticketsAndVouchersAT: ShippingValuesFields = {
    shipMethodStar: '*',
    minShipDays: 5,
    maxShipDays: 6,
    standardShip: 4.9,
    giftShip: 7.9,
    additionalCost: 2,
    minShipDaysAustria: 5,
    maxShipDaysAustria: 6,
    minShipDaysGermany: 3,
    maxShipDaysGermany: 4,
};

const erlebnisBoxAT: ShippingValuesFields = {
    shipMethodStar: '*',
    minShipDays: 5,
    maxShipDays: 6,
    standardShip: 4.9,
    minShipDaysAustria: 5,
    maxShipDaysAustria: 6,
    minShipDaysGermany: 1,
    maxShipDaysGermany: 2,
};

const giftShippingBoxDE: ShippingValuesFields = {
    ...erlebnisBoxDE,
};

const giftShippingBoxAT: ShippingValuesFields = {
    shipMethodStar: '*',
    minShipDays: 5,
    maxShipDays: 6,
    standardShip: 4.9,
    minShipDaysAustria: 5,
    maxShipDaysAustria: 6,
    minShipDaysGermany: 1,
    maxShipDaysGermany: 2,
};

const SHIPPING_VALUES_PER_TENANT: ShippingValuesPerTenant = {
    JS_DE: {
        ticketsAndVouchers: ticketsAndVouchersDE,
        erlebnisBox: erlebnisBoxDE,
        giftShippingBox: giftShippingBoxDE,
        currency: 'EUR',
    },
    JS_AT: {
        ticketsAndVouchers: ticketsAndVouchersAT,
        erlebnisBox: erlebnisBoxAT,
        giftShippingBox: { ...giftShippingBoxAT, expressShip: 16 },
        currency: 'EUR',
    },
    MD_DE: {
        ticketsAndVouchers: { ...ticketsAndVouchersDE, standardShip: 4.9 },
        erlebnisBox: { ...erlebnisBoxDE, standardShip: 4.9 },
        giftShippingBox: giftShippingBoxDE,
        currency: 'EUR',
    },
    MD_AT: {
        ticketsAndVouchers: {
            ...ticketsAndVouchersAT,
            shipMethodStar: '',
        },
        erlebnisBox: {
            ...erlebnisBoxAT,
            shipMethodStar: '',
        },
        giftShippingBox: {
            ...giftShippingBoxAT,
            shipMethodStar: '',
        },
        currency: 'EUR',
    },
    MD_CH: {
        ticketsAndVouchers: {
            ...ticketsAndVouchersAT,
            shipMethodStar: '',
            giftShip: 7.9,
        },
        erlebnisBox: {
            ...erlebnisBoxAT,
            shipMethodStar: '',
            expressShip: undefined,
        },
        giftShippingBox: {
            ...giftShippingBoxAT,
            shipMethodStar: '',
            expressShip: undefined,
        },
        currency: 'CHF',
    },
};

function getShippingValuesFromTenant(tenant: keyof ShippingValuesPerTenant): ShippingValues {
    return SHIPPING_VALUES_PER_TENANT[tenant] || { ticketsAndVouchers: {}, erlebnisBox: {} };
}

const getTenantConfig = (tenant: TenantEnum): TenantConfig => {
    return tenantConfigMapping[tenant] || tenantConfigMapping[TenantEnum.JS_DE];
};

export { emptySharedData, getShippingValuesFromTenant, getTenantConfig };
